import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, Check, ArrowRight, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { DashboardHeader } from "@/components/DashboardHeader";
import { DashboardFooter } from "@/components/DashboardFooter";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

const ClientOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: calculations } = useQuery({
    queryKey: ['calculations', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calculations')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });

  const { data: documents } = useQuery({
    queryKey: ['documents', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', user?.id);

      if (error) throw error;
      return data;
    },
  });

  const getStatus = () => {
    if (!calculations) return "pending_calculation";
    if (!documents || documents.length === 0) return "pending_documents";
    return "completed";
  };

  const status = getStatus();

  const getNextStep = () => {
    switch (status) {
      case "pending_calculation":
        return {
          text: "Complete your savings calculation",
          link: "/calculator",
          urgent: true
        };
      case "pending_documents":
        return {
          text: "Upload your required documents",
          link: "/documents",
          urgent: true
        };
      case "completed":
        return {
          text: "All steps completed! We'll contact you soon.",
          link: null,
          urgent: false
        };
    }
  };

  const nextStep = getNextStep();

  return (
    <div className="dark">
      <SidebarProvider defaultOpen={true}>
        <div className="flex flex-col min-h-screen bg-[#1A1F2C] dark:bg-[#1A1F2C] w-full">
          <DashboardHeader />
          <div className="flex flex-1 relative">
            <DashboardSidebar />
            <div className="flex-1 ml-64">
              <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))] dark:opacity-20 opacity-10" />
              <div className="relative px-6 py-8 lg:px-8 mt-16">
                <div className="mx-auto max-w-4xl">
                  <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-white">My Overview</h1>
                    <Button
                      onClick={() => navigate('/chat')}
                      className="flex items-center gap-2"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Chat with Admin
                    </Button>
                  </div>
                  
                  {/* Status Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-gray-900/50">
                      <h3 className="text-lg font-semibold text-white mb-2">Application Status</h3>
                      <div className="flex items-center gap-2">
                        {status === 'completed' ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        )}
                        <span className="text-gray-300">
                          {status === 'completed' ? 'Application Complete' : 'Application In Progress'}
                        </span>
                      </div>
                    </div>

                    {calculations && (
                      <div className="p-4 rounded-lg bg-gray-900/50">
                        <h3 className="text-lg font-semibold text-white mb-2">Potential Savings</h3>
                        <div className="space-y-2">
                          <p className="text-green-400 font-semibold">
                            R{calculations.monthly_savings.toFixed(2)} monthly
                          </p>
                          <p className="text-gray-400 text-sm">
                            {calculations.reduction_percentage}% reduction in payments
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Next Steps Card */}
                  <div className={`p-4 rounded-lg ${nextStep.urgent ? 'bg-yellow-500/10 border border-yellow-500/20' : 'bg-gray-900/50'} mb-6`}>
                    <h3 className="text-lg font-semibold text-white mb-2">Next Step</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {nextStep.urgent ? (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <Check className="h-5 w-5 text-green-500" />
                        )}
                        <span className="text-gray-300">{nextStep.text}</span>
                      </div>
                      {nextStep.link && (
                        <a 
                          href={nextStep.link} 
                          className="flex items-center gap-1 text-primary hover:text-primary/80 transition-colors"
                        >
                          Continue <ArrowRight className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Documents Overview */}
                  <div className="p-4 rounded-lg bg-gray-900/50">
                    <h3 className="text-lg font-semibold text-white mb-2">Documents ({documents?.length || 0})</h3>
                    {documents && documents.length > 0 ? (
                      <ul className="space-y-2">
                        {documents.map((doc) => (
                          <li key={doc.id} className="flex items-center gap-2 text-gray-300">
                            <Check className="h-4 w-4 text-green-500" />
                            {doc.name}
                            <span className="text-xs text-gray-500">
                              {format(new Date(doc.created_at), 'dd MMM yyyy')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-400">No documents uploaded yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DashboardFooter />
        </div>
      </SidebarProvider>
    </div>
  );
};

export default ClientOverview;